import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import sharedMessages from '../../messages';
import useAsync from 'hooks/useAsync';
import Spacer from 'components/Spacer';
import { FormikHelpers } from 'formik';
import { Button, TextLink } from '@teachstonellc/component-library';
import { sendSmsCodeForLogin } from 'actions/sms';
import { ButtonWrapper, TextButtonWrapper } from './Styled';
import { Formik, Form, PhoneInput } from 'components/EnhancedForm';
import { useLoginManager } from 'components/LoginManager';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import {
  formatLoginIdentifier,
  getDefaultPhoneCountry
} from '../../loginInput';
import { useId, useRef } from 'react';
import useFocusOnStepChange from '../../useFocusOnStepChange';

import {
  getGenericErrorMessage,
  getLockedMessage,
  isAlreadyAuthenticatedError,
  redirectToDashboard
} from '../../utils';

const validationSchema = yup.object({
  phone_number: yup
    .string()
    .required(() => i18n.ft(messages.errors.phone.required))
    .test(
      'possible-phone-number',
      () => i18n.ft(messages.errors.phone.required),
      value => Boolean(value) && isPossiblePhoneNumber(value as string)
    )
});

interface SendCodeFormProps {
  /** Callback fired when the SMS code is successfully sent. */
  onSuccess: () => void;
}

function SendCodeForm({ onSuccess }: SendCodeFormProps) {
  const { isLoading, run } = useAsync();
  const { setStep, setEmailOrPhone, emailOrPhone } = useLoginManager();
  const sendsToId = useId();
  const textMeButtonRef = useRef<HTMLButtonElement>(null);
  useFocusOnStepChange(textMeButtonRef);

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    run(sendSmsCodeForLogin(values), {
      onSuccess: () => {
        setEmailOrPhone(values.phone_number);
        onSuccess();
      },
      onError: (error: any) => {
        if (isAlreadyAuthenticatedError(error)) {
          redirectToDashboard();
          return;
        }

        formik.setFieldError(
          'phone_number',
          getLockedMessage(error) ??
            error?.errors?.phone_number?.[0] ??
            getGenericErrorMessage()
        );
      }
    });
  }

  function handleTypePassword(phone: string) {
    return () => {
      setEmailOrPhone(phone);
      setStep('loginWithPhone');
    };
  }

  function handleBackToLogin(phone: string) {
    return () => {
      setEmailOrPhone(phone);
      setStep('login');
    };
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={{ phone_number: emailOrPhone }}
      validateOnBlur={false}
    >
      {formik => (
        <Form>
          <PhoneInput
            name="phone_number"
            label={i18n.ft(messages.fields.phoneNumber)}
            defaultCountry={getDefaultPhoneCountry()}
          />

          <ButtonWrapper>
            <Button
              ref={textMeButtonRef}
              fullWidth
              variant="primary"
              shape="rounded"
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              aria-describedby={
                formik.values.phone_number ? sendsToId : undefined
              }
            >
              {i18n.ft(messages.textMe)}
            </Button>
            {formik.values.phone_number && (
              <span id={sendsToId} className="sr-only">
                {i18n.ft(messages.sendsTo, {
                  phone: formatLoginIdentifier(formik.values.phone_number)
                })}
              </span>
            )}
          </ButtonWrapper>

          <TextButtonWrapper>
            <TextLink
              as="button"
              type="button"
              onClick={handleTypePassword(formik.values.phone_number)}
            >
              {i18n.ft(messages.usePassword)}
            </TextLink>
            <Spacer size={16} />
            <TextLink
              as="button"
              type="button"
              onClick={handleBackToLogin(formik.values.phone_number)}
            >
              {i18n.ft(sharedMessages.useDifferentLogin)}
            </TextLink>
          </TextButtonWrapper>
        </Form>
      )}
    </Formik>
  );
}

export default SendCodeForm;
